import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Flame, FileDown, Upload, X, Camera, CheckCircle2, FileText, Loader2 } from "lucide-react";

function digits(v) { return (v || "").replace(/\D/g, ""); }
function maskCPF(v) {
  const c = digits(v).slice(0, 11);
  if (c.length <= 3) return c;
  if (c.length <= 6) return `${c.slice(0,3)}.${c.slice(3)}`;
  if (c.length <= 9) return `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6)}`;
  return `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6,9)}-${c.slice(9)}`;
}

export default function PSSInscription() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ cpf: "", rg: "", address: "", email: "", edital_accepted: false });
  const [cv, setCv] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfieUrl, setSelfieUrl] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [result, setResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/pss/inscricao/${token}`).then((r) => {
      if (!r.ok) throw new Error("Link inválido ou expirado");
      return r.json();
    }).then((d) => {
      setData(d);
      if (d.candidate?.status === "concluido") setResult({ protocol: d.candidate.protocol, name: d.candidate.name });
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOn(true);
    } catch { toast.error("Não foi possível acessar a câmera. Você pode enviar uma foto do arquivo."); }
  };

  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; } setCameraOn(false); };

  const capture = () => {
    const v = videoRef.current, canvas = canvasRef.current;
    if (!v || !canvas) return;
    canvas.width = v.videoWidth || 640; canvas.height = v.videoHeight || 480;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) { setSelfieBlob(blob); setSelfieUrl(URL.createObjectURL(blob)); stopCamera(); } }, "image/jpeg", 0.85);
  };

  const uploadSelfieFile = (e) => {
    const f = (e.target.files || [])[0]; if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Envie uma imagem.");
    setSelfieBlob(f); setSelfieUrl(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (digits(form.cpf).length !== 11) return toast.error("CPF inválido.");
    if (!form.rg.trim()) return toast.error("Informe o RG.");
    if (!cv) return toast.error("Envie o currículo.");
    if (!selfieBlob) return toast.error("Envie ou tire uma selfie.");
    if (!form.edital_accepted) return toast.error("É obrigatório concordar com o edital.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("cpf", digits(form.cpf));
      fd.append("rg", form.rg);
      fd.append("address", form.address);
      fd.append("email", form.email);
      fd.append("edital_accepted", "true");
      fd.append("cv", cv, cv.name);
      docs.forEach((d) => fd.append("documento", d, d.name));
      fd.append("selfie", selfieBlob, "selfie.jpg");
      const res = await fetch(`${API}/pss/inscricao/${token}`, { method: "POST", body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({ detail: "Erro" })); throw new Error(typeof j.detail === "string" ? j.detail : formatApiError({ response: { data: j } })); }
      const j = await res.json();
      setResult({ protocol: j.protocol, name: data.candidate.name });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center text-forest-900/70"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...</div>;
  if (!data) return <div className="container mx-auto px-6 py-16 text-center"><h1 className="font-display text-3xl">Link inválido</h1></div>;

  const cand = data.candidate; const proc = data.process;

  if (result) {
    return (
      <div className="container mx-auto px-6 py-16 max-w-3xl" data-testid="pss-success">
        <div className="bg-cream-100 border border-forest-700 p-8">
          <div className="flex items-center gap-3 mb-2"><CheckCircle2 className="w-7 h-7 text-forest-700" /><h1 className="font-display text-3xl text-forest-900">Inscrição confirmada!</h1></div>
          <p className="mt-2 text-forest-900/80">Olá {result.name.split(" ")[0]}, sua inscrição no <b>{proc.code}</b> foi registrada com sucesso.</p>
          <div className="mt-6 bg-white border border-cream-400 p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700">Protocolo de inscrição</div>
            <div className="font-mono text-2xl text-forest-900 mt-1" data-testid="success-protocol">{result.protocol}</div>
          </div>
          <p className="mt-4 text-sm text-forest-900/75">Guarde este número. A Comissão do PSS analisará sua documentação e a COOMFLONA entrará em contato.</p>
          <Link to="/" className="mt-6 inline-block px-5 py-2 bg-forest-700 text-cream-100 hover:bg-forest-900">Voltar ao site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-10 max-w-3xl" data-testid="pss-inscription-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center"><Flame className="w-5 h-5" /></div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">{proc.code}</div>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 leading-tight">{proc.title}</h1>
        </div>
      </div>
      <details className="bg-cream-100 border border-forest-700/30 mb-5" data-testid="edital-block">
        <summary className="cursor-pointer list-none px-5 py-4 flex items-center gap-2"><FileText className="w-4 h-4" /> Ver Edital completo do Processo Seletivo</summary>
        <div className="px-5 pb-5 text-sm text-forest-900/85 whitespace-pre-wrap max-h-[300px] overflow-y-auto border-t border-cream-400 pt-4">{proc.edital_content}</div>
        <div className="px-5 pb-4"><a href={`${API}/pss/processes/${proc.code}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-forest-700 hover:text-copaiba-700"><FileDown className="w-4 h-4" /> Baixar Edital (PDF)</a></div>
      </details>

      <div className="bg-white border border-cream-400 p-5 mb-5">
        <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Seus dados (pré-cadastro)</div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><span className="text-forest-900/65 text-xs uppercase">Nome</span><div className="font-medium">{cand.name}</div></div>
          <div><span className="text-forest-900/65 text-xs uppercase">Comunidade</span><div>{cand.community || "—"}</div></div>
          <div><span className="text-forest-900/65 text-xs uppercase">Telefone</span><div>{cand.phone_display}</div></div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-cream-400 p-6 space-y-5" data-testid="pss-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field l="CPF *"><input required value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} data-testid="pss-cpf" className="inp" placeholder="000.000.000-00" /></Field>
          <Field l="RG *"><input required value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} data-testid="pss-rg" className="inp" placeholder="0000000 SSP-PA" /></Field>
        </div>
        <Field l="Endereço"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="pss-address" className="inp" placeholder="Rua, número, comunidade" /></Field>
        <Field l="E-mail (opcional)"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pss-email" className="inp" /></Field>

        <Field l="Currículo (PDF ou imagem) *">
          {cv ? (
            <div className="flex items-center gap-2 bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
              <FileText className="w-4 h-4 text-forest-700" /><span className="flex-1">{cv.name}</span>
              <button type="button" onClick={() => setCv(null)} className="text-terracotta-500"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-dashed border-forest-700 text-forest-900 hover:bg-cream-200 text-sm">
              <Upload className="w-4 h-4" /> Anexar currículo
              <input type="file" accept=".pdf,image/*" onChange={(e) => setCv((e.target.files || [])[0])} data-testid="pss-cv" className="hidden" />
            </label>
          )}
        </Field>

        <Field l="Documentos exigidos (declarações, fotos, RG, comprovante de escolaridade, etc.)">
          {Array.isArray(proc.required_docs) && proc.required_docs.length > 0 && (
            <div className="bg-copaiba-100 border border-copaiba-300 p-3 mb-3 text-xs text-forest-900" data-testid="required-docs-list">
              <div className="font-medium mb-2">📋 Documentos obrigatórios que você precisa anexar:</div>
              <ol className="list-decimal ml-5 space-y-1">
                {proc.required_docs.map((d, i) => <li key={i}>{d}</li>)}
              </ol>
              <div className="mt-2 text-forest-900/70">
                Você pode anexar <b>fotos</b> (JPG/PNG) e <b>PDFs</b> das declarações. Até 8 arquivos, máx. 8MB cada.
              </div>
            </div>
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-dashed border-cream-400 hover:border-forest-700 text-forest-900 text-sm">
            <Upload className="w-4 h-4" /> Anexar declarações e fotos
            <input type="file" multiple accept=".pdf,image/*" onChange={(e) => setDocs([...docs, ...Array.from(e.target.files || [])].slice(0, 8))} data-testid="pss-docs" className="hidden" />
          </label>
          {docs.length > 0 && <ul className="mt-2 space-y-1 text-sm">{docs.map((d, i) => (
            <li key={i} className="flex items-center justify-between bg-cream-100 border border-cream-400 px-3 py-1.5">
              <span>{d.name}</span>
              <button type="button" onClick={() => setDocs(docs.filter((_, j) => j !== i))} className="text-terracotta-500"><X className="w-4 h-4" /></button>
            </li>
          ))}</ul>}
        </Field>

        <Field l="Selfie para identificação *">
          {selfieUrl ? (
            <div className="flex items-center gap-3">
              <img src={selfieUrl} alt="selfie" className="w-28 h-28 object-cover border-2 border-forest-700" data-testid="pss-selfie-preview" />
              <button type="button" onClick={() => { setSelfieBlob(null); setSelfieUrl(null); }} className="text-sm text-terracotta-500 underline">Refazer</button>
            </div>
          ) : cameraOn ? (
            <div className="space-y-2">
              <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-sm bg-black border border-cream-400" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-2">
                <Button type="button" onClick={capture} data-testid="pss-selfie-capture" className="bg-forest-700 text-cream-100 rounded-none"><Camera className="w-4 h-4 mr-2" /> Tirar selfie</Button>
                <Button type="button" onClick={stopCamera} variant="outline" className="rounded-none">Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" onClick={startCamera} data-testid="pss-selfie-start" className="bg-forest-700 text-cream-100 rounded-none"><Camera className="w-4 h-4 mr-2" /> Abrir câmera</Button>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-cream-400 text-forest-900 hover:bg-cream-200 text-sm">
                <Upload className="w-4 h-4" /> Ou anexar foto
                <input type="file" accept="image/*" capture="user" onChange={uploadSelfieFile} data-testid="pss-selfie-file" className="hidden" />
              </label>
            </div>
          )}
        </Field>

        <label className="flex items-start gap-2 cursor-pointer bg-cream-200 border border-cream-400 p-3">
          <input type="checkbox" checked={form.edital_accepted} onChange={(e) => setForm({ ...form, edital_accepted: e.target.checked })} data-testid="pss-accept" className="mt-1 w-4 h-4 accent-forest-700" />
          <span className="text-sm text-forest-900">☑️ Li e concordo com o <b>Edital do Processo Seletivo Simplificado</b> da COOMFLONA.</span>
        </label>

        <Button type="submit" disabled={submitting} data-testid="pss-submit" className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base">
          {submitting ? "Enviando..." : "Confirmar inscrição"}
        </Button>
      </form>
    </div>
  );
}

function Field({ l, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">{l}</span><div className="mt-2">{children}</div></label>;
}

if (typeof document !== "undefined" && !document.getElementById("pss-inp-style")) {
  const s = document.createElement("style"); s.id = "pss-inp-style";
  s.innerHTML = `.inp{display:block;width:100%;background:#FDFAF1;border:1px solid #E8DFCB;padding:.7rem;color:#1A3620;font-size:.95rem;outline:none}.inp:focus{border-color:#1A3620}`;
  document.head.appendChild(s);
}
